import { CommonModule, DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { NavigationEnd, RouterOutlet, Scroll } from '@angular/router';
import { Router } from '@angular/router';
import { AppService } from '@core/services/app/app-api';
import { SearchService } from '@core/services/search/search-api';
import { TokenHolderService } from '@core/services/token-holder/token-holder-api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppState, AuthService } from '@auth0/auth0-angular';
import { setFetchingUserData } from '@core/store/actions/common.actions';
import { Store } from '@ngrx/store';
import { AnalyticsService } from '@core/services/analytics/analytics-api';
import { MatSelectPanelScrollService } from '@core/services/mat-select-panel-scroll/mat-select-panel-scroll.service';
import { AvantorTabBlockerComponent } from '@shared/components/avantor-tab-blocker/avantor-tab-blocker';
import { SpinnerComponent } from './layout/spinner/spinner';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrls: ['./app.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    AvantorTabBlockerComponent,
    SpinnerComponent
    , TranslateModule]
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'avantor-inventory-manager-ui';
  private routerNavEndSubscription?: Subscription;
  private wheelListenerCleanup?: () => void;

  constructor(
    // eslint-disable-next-line no-unused-vars
    private appService: AppService,
    private translate: TranslateService,
    private tokenHolderService: TokenHolderService,
    private router: Router,
    private analyticsService: AnalyticsService,
    private matSelectPanelScrollService: MatSelectPanelScrollService,
    viewportScroller: ViewportScroller,
    private searchService: SearchService,
    private auth0: AuthService,
    private store: Store<AppState>,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    private renderer: Renderer2
  ) {
    router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        if (!event.urlAfterRedirects.includes('search-results')) {
          searchService.setKeyword('');
        }
      }
    });
    this.routerNavEndSubscription = router.events
      .pipe(filter((event: any): event is Scroll => event instanceof Scroll))
      .subscribe((event: Scroll) => {
        if (event.position) {
          viewportScroller.scrollToPosition(event.position);
        } else if (event.anchor) {
          viewportScroller.scrollToAnchor(event.anchor);
        } else {
          // eslint-disable-next-line no-undef
          setTimeout(() => {
            viewportScroller.scrollToPosition([0, 0]);
          }, 500);

        }
      });
    // try {
    //   const script = document.createElement('script');
    //   script.type = 'text/javascript';
    //   if (environment.production) {
    //     script.src = './js/apm.prod.js';
    //   } else {
    //     script.src = './js/apm.stage.js';
    //   }
    //   script.onerror = () => console.warn('[APM] Script not found — skipping APM');
    //   document.head.appendChild(script);
    // } catch (e) {
    //   console.warn('[APM] Could not load APM script', e);
    // }
  }

  ngOnInit() {
    console.log("app started 22/6/26");
    this.matSelectPanelScrollService.start();
    this.attachAxisLockedWheelScroll();
    this.initUserData();
    this.loadScript();
    this.appService.loadClarityAndGTM();
    let StockroomAndOrgData = JSON.parse(sessionStorage.getItem("cStockroomAndOrg")!);
    if (StockroomAndOrgData?.organization) {
      this.analyticsService.sendDetailsForLoginUserTracking(StockroomAndOrgData.organization.orgName)
    }
  }

  ngOnDestroy(): void {
    this.routerNavEndSubscription?.unsubscribe();
    this.matSelectPanelScrollService.stop();
    this.wheelListenerCleanup?.();
  }

  private attachAxisLockedWheelScroll(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      if (event.defaultPrevented || event.ctrlKey) {
        return;
      }

      const absX = Math.abs(event.deltaX);
      const absY = Math.abs(event.deltaY);

      // Let normal single-axis scrolling behave exactly as the browser provides it.
      if (absX === 0 || absY === 0) {
        return;
      }

      const target = event.target instanceof Element ? event.target : document.body;
      const axis = absX > absY ? 'x' : 'y';
      const scrollHost = this.getScrollableAncestor(target, axis);

      if (!scrollHost) {
        return;
      }

      event.preventDefault();

      if (axis === 'x') {
        scrollHost.scrollLeft += this.normalizeWheelDelta(event.deltaX, event.deltaMode);
        return;
      }

      scrollHost.scrollTop += this.normalizeWheelDelta(event.deltaY, event.deltaMode);
    };

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    this.wheelListenerCleanup = () =>
      window.removeEventListener('wheel', onWheel, { capture: true } as EventListenerOptions);
  }

  private getScrollableAncestor(target: Element, axis: 'x' | 'y'): HTMLElement | null {
    const scrollingElement = document.scrollingElement as HTMLElement | null;
    let current: Element | null = target;

    while (current) {
      if (current instanceof HTMLElement && this.canScroll(current, axis)) {
        return current;
      }

      current = current.parentElement;
    }

    if (scrollingElement && this.canScroll(scrollingElement, axis)) {
      return scrollingElement;
    }

    return document.documentElement;
  }

  private canScroll(element: HTMLElement, axis: 'x' | 'y'): boolean {
    const styles = window.getComputedStyle(element);
    const overflow = axis === 'x' ? styles.overflowX : styles.overflowY;
    const allowsScroll = overflow === 'auto' || overflow === 'scroll' || overflow === 'overlay';

    if (!allowsScroll) {
      return false;
    }

    return axis === 'x'
      ? element.scrollWidth > element.clientWidth
      : element.scrollHeight > element.clientHeight;
  }

  private normalizeWheelDelta(delta: number, deltaMode: number): number {
    if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
      return delta * 16;
    }

    if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
      return delta * window.innerHeight;
    }

    return delta;
  }

  private loadScript(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const scriptId = 'apm-script';
    if (this.document.getElementById(scriptId)) {
      return;
    }

    const script = this.renderer.createElement('script') as HTMLScriptElement;
    script.id = scriptId;
    script.type = 'text/javascript';
    script.async = true;
    script.src = environment.production ? 'js/apm.prod.js' : 'js/apm.stage.js';
    script.onerror = () => console.warn('[APM] Script not found - skipping APM');

    this.renderer.appendChild(this.document.head, script);
  }

  /**
   * @INIT_USER_DATA
   * - This will trigger userData request
   * for persistent and up to date account data
   */
  initUserData() {
    if (!localStorage.getItem('renderingBatchEnableProducts') && !localStorage.getItem('renderingDifferentSectionAccountSettings')) {
      localStorage.setItem('renderingBatchEnableProducts', 'EXPAND_ALL');
      localStorage.setItem('renderingDifferentSectionAccountSettings', 'COLLAPSE_ALL');
    }
    const isTokenRefreshed = sessionStorage.getItem('tokenRefresh') === 'true';
    if (isTokenRefreshed) {
      this.auth0.idTokenClaims$.subscribe((tokenClaims) => {
        if (tokenClaims) {
          this.tokenHolderService.setAuthToken(tokenClaims.__raw);
          sessionStorage.setItem('tokenRefresh', 'false')
          this.proceedWithUserData();
        }
      });

    } else {
      this.proceedWithUserData();
    }

    // let isLoggedIn = this.tokenHolderService.getAuthToken();
    // if (isLoggedIn) {
    //   this.store.dispatch(setFetchingUserData({ status: true }));
    //   this.appService.initUserData().then(res => {
    //     this.store.dispatch(setFetchingUserData({ status: false }));

    //     this.appService.setUserData(res);
    //   }).catch((err) => {
    //     this.store.dispatch(setFetchingUserData({ status: false }));
    //     if (err.status == 404) {
    //       this.router.navigate(['/access-denied']);
    //     }
    //   });
    // }
  }

  proceedWithUserData() {
    let isLoggedIn = this.tokenHolderService.getAuthToken();
    if (isLoggedIn) {
      this.store.dispatch(setFetchingUserData({ status: true }));
      this.appService.initUserData().then(res => {
        this.store.dispatch(setFetchingUserData({ status: false }));
        this.applyUserData(res);
      }).catch((err) => {
        this.store.dispatch(setFetchingUserData({ status: false }));
        if (err.status == 404) {
          this.router.navigate(['/access-denied']);
        }
      });
    }
  }

  private applyUserData(userData: any) {
    if (userData?.language) {
      this.translate.use(userData.language);
    }
    this.appService.setUserData(userData);
  }

  initTranslate() {
    this.translate.use('zh');
  }
}

